import { Button, Card, Divider, QRCode, Radio, Select } from 'antd';
import {
  CreateRecyclingConsignmentOrderV2SchemaType,
  PRODUCT_REMARK_DEFAULT_VALUES,
} from '../common';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { Package, Plus, Trash2 } from 'lucide-react';
import ProductCategory from './form-items/category';
import BrandName from './form-items/brand-name';
import ProductName from './form-items/name';
import Rank from './form-items/rank';
import RankDesc from './form-items/rank-desc';
import { generateInitSelectedRankDescOptions } from 'pages/product/create-page-v2/common';
import Color from './form-items/color';
import Material from './form-items/material';
import Hardware from './form-items/hardware';
import Stamp from './form-items/stamp';
import FullStamp from './form-items/full-stamp';
import Accessory from './form-items/accessory';
import FinalSalePrice from './form-items/final-sale-price';
import FinalRecyclePrice from './form-items/final-recycle-price';
import Price from './form-items/price';
import FirstValuationShopRemark from './form-items/first-valuation-shop-remark';
import GuestRemarks from './form-items/guest-remarks';
import FirstValuationStock from './form-items/first-valuation-stock';
import FirstValuationStockRemark from './form-items/first-valuation-stock-remark';
import StoreReceiptPics from './form-items/store-receipt-pics';
import {
  getBagTypeNameFromCategoryIdList,
  isBagProductCategory,
  isBkcProductCategory,
} from './form-items/authentication-config';
import { useAppSelector } from 'store/hooks';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useMemo, useEffect, useRef } from 'react';
import { FormLabel } from '../form-label';
import type { AiIdentifyPicsRef } from './form-items/ai-identify-pics';
import { bindAuthenticationTaskToProduct } from './authentication-task-product';
import { serializeProductQrPayload } from './product-qr';
import Quantity from './form-items/quantity';
import { isTwillyProductCategory } from '../twilly';
import classNames from 'classnames';
import ProductRemark from './form-items/product-remark';
import FakeBagReason from './form-items/fake-bag-reason';
import { getSelectableStaffOptions } from 'pages/rrs/recycling-consignment-detail/modify-base-info.utils';

// 单个产品项组件，用于处理 BKC 判断
const ProductItem = ({
  control,
  setValue,
  index,
  fieldId,
  fieldsLength,
  onRemove,
  onAiIdentifyRefChange,
}: {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  setValue: UseFormSetValue<CreateRecyclingConsignmentOrderV2SchemaType>;
  index: number;
  fieldId: string;
  fieldsLength: number;
  onRemove: () => void;
  onAiIdentifyRefChange?: (
    fieldId: string,
    ref: AiIdentifyPicsRef | null,
  ) => void;
}) => {
  const {
    productCategoryCascaderOptions,
    colorSelectOptions,
    staffSelectOptions,
  } = useAppSelector(selectGlobalInfo);
  const activeStaffOptions = useMemo(
    () => getSelectableStaffOptions(staffSelectOptions),
    [staffSelectOptions],
  );

  const productCategoryIdList = useWatch({
    control,
    name: `productList.${index}.productCategoryIdList`,
  });
  const product = useWatch({
    control,
    name: `productList.${index}`,
  });
  const isFakeBag = useWatch({
    control,
    name: `productList.${index}.isFakeBag`,
  });

  // 根据 productCategoryIdList 实时计算包型名称
  const bagTypeName = useMemo(
    () =>
      getBagTypeNameFromCategoryIdList(
        productCategoryIdList,
        productCategoryCascaderOptions,
      ),
    [productCategoryIdList, productCategoryCascaderOptions],
  );

  // 一级分类 value === 'bags' 才算包类；二级分类 value 属于 birkin/kelly/constance 才算 BKC
  const hasBagCategorySelected = isBagProductCategory(
    productCategoryIdList,
    productCategoryCascaderOptions,
  );
  const isBKC = isBkcProductCategory(
    productCategoryIdList,
    productCategoryCascaderOptions,
  );
  const isTwilly = useMemo(
    () =>
      isTwillyProductCategory(
        productCategoryIdList,
        productCategoryCascaderOptions,
      ),
    [productCategoryCascaderOptions, productCategoryIdList],
  );

  // 当包款是BKC且非假包时,默认初始化XM资产为"是";
  // 非BKC或判定为假包时,默认/强制为"否",且假包状态下不允许手动修改
  useEffect(() => {
    setValue(`productList.${index}.initXmAssets`, !isFakeBag && isBKC);
  }, [isBKC, isFakeBag, index, setValue]);

  // 包型变化时（包括不同包型之间切换、或切换到非包类分类），
  // 清空人工复核相关字段，避免遗留上一个包型的判定结果
  const prevBagTypeNameRef = useRef(bagTypeName);
  useEffect(() => {
    if (prevBagTypeNameRef.current === bagTypeName) return;
    prevBagTypeNameRef.current = bagTypeName;
    setValue(`productList.${index}.isFakeBag`, undefined);
    setValue(`productList.${index}.fakeBagReason`, '');
    setValue(`productList.${index}.reviewerName`, undefined);
  }, [bagTypeName, index, setValue]);

  const productQrValue = useMemo(
    () => (product ? serializeProductQrPayload(product) : ''),
    [product],
  );

  const initXmAssetsRadio = (
    <Controller
      name={`productList.${index}.initXmAssets`}
      control={control}
      render={({ field }) => (
        <div className="flex items-center gap-2">
          <FormLabel>{i18n.t(LOCALS.init_xm_assets)}:</FormLabel>
          <Radio.Group
            value={field.value ?? false}
            disabled={isFakeBag}
            onChange={(e) => field.onChange(e.target.value)}
          >
            <Radio value={true}>{i18n.t(LOCALS.yes)}</Radio>
            <Radio value={false}>{i18n.t(LOCALS.no)}</Radio>
          </Radio.Group>
        </div>
      )}
    />
  );

  return (
    <div>
      <div className="flex items-center relative">
        <Divider orientation="center">
          <div className="text-xl">No.{index + 1}</div>
        </Divider>
        <div className="absolute right-0 flex items-center gap-3">
          {fieldsLength > 1 && (
            <Trash2
              onClick={onRemove}
              className="text-xl cursor-pointer hover:text-red-500"
            />
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div
          className={classNames('grid gap-2', {
            'grid-cols-2': !isTwilly,
            'grid-cols-3': isTwilly,
          })}
        >
          <ProductCategory
            control={control}
            index={index}
            setValue={setValue}
          />
          <BrandName control={control} index={index} />
          {isTwilly && <Quantity control={control} index={index} />}
        </div>

        <ProductName control={control} index={index} />

        <Rank control={control} index={index} setValue={setValue} />

        <RankDesc control={control} index={index} setValue={setValue} />

        <div className="grid grid-cols-2 gap-2">
          <Color control={control} index={index} setValue={setValue} />
          <ProductRemark
            control={control}
            index={index}
            name="colorRemark"
            label={i18n.t(LOCALS.color_remark)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Material control={control} index={index} setValue={setValue} />
          <ProductRemark
            control={control}
            index={index}
            name="materialRemark"
            label={i18n.t(LOCALS.material_remark)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Hardware control={control} index={index} />
          <ProductRemark
            control={control}
            index={index}
            name="hardwareRemark"
            label={i18n.t(LOCALS.hardware_remark)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Stamp control={control} index={index} />
          <ProductRemark
            control={control}
            index={index}
            name="stampRemark"
            label={i18n.t(LOCALS.stamp_remark)}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <FullStamp control={control} index={index} />
        </div>

        <Accessory control={control} index={index} required={isBKC} />

        {isBKC && productQrValue && (
          <div className="flex justify-center">
            <QRCode value={productQrValue} size={150} errorLevel="M" />
          </div>
        )}

        <StoreReceiptPics
          control={control}
          index={index}
          bagTypeName={bagTypeName}
          isBKC={isBKC}
          fieldId={fieldId}
          onAiIdentifyRefChange={onAiIdentifyRefChange}
          onBindSelect={(item) => {
            setValue(
              `productList.${index}`,
              bindAuthenticationTaskToProduct({
                currentProduct: product,
                taskData: item.data || {},
                authenticationBagId: item.authenticationBagId,
                productCategoryCascaderOptions,
                colorSelectOptions,
                reviewStatus: item.result?.reviewStatus,
              }),
              {
                shouldDirty: true,
                shouldValidate: true,
              },
            );
          }}
        />

        {hasBagCategorySelected && (
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-start gap-2">
              <FormLabel required>{i18n.t(LOCALS.manual_review)}:</FormLabel>
              <Controller
                name={`productList.${index}.isFakeBag`}
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1">
                    <Radio.Group
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        if (e.target.value === false) {
                          setValue(`productList.${index}.fakeBagReason`, '');
                        }
                      }}
                    >
                      <Radio value={false}>{i18n.t(LOCALS.is_real_bag)}</Radio>
                      <Radio value={true}>{i18n.t(LOCALS.is_fake_bag)}</Radio>
                    </Radio.Group>
                    <div className="text-xs text-gray-500 leading-5">
                      {i18n.t(LOCALS.ai_identify_manual_review_notice)}
                    </div>
                    {fieldState.error?.message && (
                      <div className="text-xs text-red-500 leading-5">
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            <div className="flex items-center gap-2">
              <FormLabel required>
                {i18n.t(LOCALS.manual_review_confirmer)}:
              </FormLabel>
              <Controller
                name={`productList.${index}.reviewerName`}
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1 flex-1">
                    <Select
                      className="w-full"
                      showSearch
                      filterOption={(input, option) =>
                        String(option?.label || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      {...field}
                      status={fieldState.error ? 'error' : undefined}
                      options={activeStaffOptions.map((option) => ({
                        value: option.label,
                        label: option.label,
                      }))}
                    />
                    {fieldState.error?.message && (
                      <div className="text-xs text-red-500 leading-5">
                        {fieldState.error.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {hasBagCategorySelected && isFakeBag && (
          <FakeBagReason control={control} index={index} />
        )}

        <div className="grid grid-cols-2 gap-2">
          <FinalSalePrice control={control} index={index} />
          <FinalRecyclePrice control={control} index={index} />
        </div>

        <Price control={control} index={index} />

        <div className="grid grid-cols-2 gap-2">
          <FirstValuationShopRemark control={control} index={index} />
          <GuestRemarks control={control} index={index} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-6">
            <FirstValuationStock control={control} index={index} />
            {initXmAssetsRadio}
          </div>
          <FirstValuationStockRemark control={control} index={index} />
        </div>
      </div>
    </div>
  );
};

const ProductList = ({
  control,
  setValue,
  onAiIdentifyRefChange,
}: {
  control: Control<CreateRecyclingConsignmentOrderV2SchemaType>;
  setValue: UseFormSetValue<CreateRecyclingConsignmentOrderV2SchemaType>;
  onAiIdentifyRefChange?: (
    fieldId: string,
    ref: AiIdentifyPicsRef | null,
  ) => void;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productList',
  });

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 text-base font-medium">
        <Package className="w-5 h-5" />
        {i18n.t(LOCALS.product_info)}
      </div>

      <div className="space-y-4 mb-8">
        {fields.map((field, index) => (
          <ProductItem
            key={field.id}
            control={control}
            setValue={setValue}
            index={index}
            fieldId={field.id}
            fieldsLength={fields.length}
            onRemove={() => remove(index)}
            onAiIdentifyRefChange={onAiIdentifyRefChange}
          />
        ))}
      </div>
      <Button
        type="primary"
        className="flex items-center gap-2 w-full justify-center text-lg"
        size="large"
        onClick={() => {
          append({
            id: Date.now().toString(),
            productCategoryIdList: [],
            brandName: 'Hermes',
            quantity: 1,
            name: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            rank: '',
            rankDesc: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            attrSize: {
              en: '',
              ja: '',
              zh_CN: '',
              zh_TW: '',
            },
            selectedRankDescOptions: generateInitSelectedRankDescOptions(),
            color: [],
            colors: '',
            ...PRODUCT_REMARK_DEFAULT_VALUES,
            material: [],
            hardware: [],
            stamp: '',
            fullStamp: '',
            accessory: [],
            firstValuationShopRemark: '',
            guestRemarks: '',
            firstValuationStock: 1,
            firstValuationStockRemark: '',
            productPics: '{"front":"","back":"","interior":"","blindStamp":""}',
            storeReceiptPics: [],
            aiIdentifyPics: [],
            bagTypeName: undefined,
            isFakeBag: undefined,
            fakeBagReason: '',
            initXmAssets: false,
          });
        }}
      >
        <Plus className="w-5 h-5" />
        {i18n.t(LOCALS.add)}
      </Button>
    </Card>
  );
};

export default ProductList;
